import { DEFAULT_TRANSITION_EASING, ease } from './easing';
import type { ClipData, Crop, GroupMotion, Keyframe, TrackData, TransitionRef } from './types';
import { KEYFRAME_PROPERTIES, keyframeDef, splitNodeProperty } from './catalog';
import { readExt, type FadeShape } from './ext';
import { gradeNodes, nodeOfProperty } from './nodes';

/** Tolerance used when comparing timeline positions (µs). */
export const TIME_EPS = 500;

export const US = 1_000_000;

export const clipEnd = (c: { startUs: number; durationUs: number }) => c.startUs + c.durationUs;

/** Snap a timeline time to the nearest frame boundary. */
export function snapToFrame(us: number, fps: number): number {
  const frame = Math.round((us * fps) / US);
  return Math.round((frame * US) / fps);
}

export function frameDurationUs(fps: number): number {
  return US / fps;
}

export function formatTimecode(us: number, fps: number): string {
  const sign = us < 0 ? '-' : '';
  const abs = Math.abs(us);
  const totalFrames = Math.floor((abs * fps) / US + 1e-6);
  const fpsInt = Math.round(fps);
  const frames = totalFrames % fpsInt;
  const totalSeconds = Math.floor(abs / US);
  const s = totalSeconds % 60;
  const m = Math.floor(totalSeconds / 60) % 60;
  const h = Math.floor(totalSeconds / 3600);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${sign}${pad(h)}:${pad(m)}:${pad(s)}:${pad(frames)}`;
}

/** A time (UTC) for file names, minute precision: "2026-10-25_1347". */
export function fileStamp(ms: number): string {
  const at = new Date(ms).toISOString();
  return `${at.slice(0, 10)}_${at.slice(11, 13)}${at.slice(14, 16)}`;
}

/**
 * A file name typed by a person, ready for an extension: no characters that
 * break paths or downloads, no extension of its own; '' when nothing is left.
 */
export function cleanFileName(name: string, ext: string): string {
  return name
    .replace(/[\\/:*?"<>|\u0000-\u001f]+/g, ' ')
    .replace(new RegExp(`\\.${ext}$`, 'i'), '')
    .replace(/\s+/g, ' ')
    .replace(/^[.\s]+|[.\s]+$/g, '')
    .slice(0, 200)
    .trim();
}

export function formatDuration(us: number): string {
  const totalSeconds = Math.max(0, Math.round(us / US));
  const s = totalSeconds % 60;
  const m = Math.floor(totalSeconds / 60) % 60;
  const h = Math.floor(totalSeconds / 3600);
  const pad = (n: number) => String(n).padStart(2, '0');
  return h > 0 ? `${h}:${pad(m)}:${pad(s)}` : `${m}:${pad(s)}`;
}

// ---------------------------------------------------------------------------
// Transitions
// ---------------------------------------------------------------------------

export interface TransitionWindow {
  /** Incoming clip, or null when the outgoing clip transitions into transparency. */
  toClipId: string | null;
  /** Outgoing clip, or null when transitioning from transparency. */
  fromClipId: string | null;
  kind: string;
  startUs: number;
  endUs: number;
  /** How progress runs through the window (see transitionProgress); 'linear' by default. */
  easing: string;
  /** Dips to black / white only: which way the dip runs (see DipMode). */
  dip?: DipMode;
}

/**
 * Which way a dip to black / white runs: through the colour (outgoing →
 * colour → incoming), into it (outgoing → colour, then a cut to the
 * incoming side) or out of it (a cut to the colour, then colour → incoming).
 * A missing side is transparent.
 */
export type DipMode = 'through' | 'to' | 'from';
export const DIP_MODES = ['through', 'to', 'from'] as const;
export const DIP_MODE_LABELS: Record<DipMode, string> = { through: 'Through the colour', to: 'Into the colour', from: 'Out of the colour' };

export const isDipTransition = (kind: string) => kind === 'fadeblack' || kind === 'fadewhite';

/**
 * Which way a clip's incoming or outgoing dip runs. Unset: through the colour
 * at a cut, into it at a clip's end (the picture fades to the colour) and out
 * of it at a clip's start (the colour fades to the picture).
 */
export function transitionDipOf(clip: Partial<Pick<ClipData, 'ext'>>, dir: 'in' | 'out', atCut: boolean): DipMode {
  return readExt(clip).transitionDip?.[dir] ?? (atCut ? 'through' : dir === 'in' ? 'from' : 'to');
}

type TimedClip = Pick<ClipData, 'id' | 'trackId' | 'startUs' | 'durationUs' | 'transitionIn' | 'transitionOut' | 'ext'>;

/** Easing of a clip's incoming or outgoing transition (stored in its ext settings). */
export function transitionEasingOf(clip: Partial<Pick<ClipData, 'ext'>>, dir: 'in' | 'out'): string {
  return readExt(clip).transitionEasing?.[dir] ?? DEFAULT_TRANSITION_EASING;
}

/**
 * Where a transition at a cut sits: centred on it, starting at it (the
 * incoming clip's head), ending at it (the outgoing clip's tail), or a custom
 * centre offset (µs after the cut) once it was dragged. Kept in the incoming
 * clip's ext settings; absent = centred.
 */
export type TransitionAlign = 'center' | 'start' | 'end' | number;

export const TRANSITION_ALIGNS = ['center', 'start', 'end'] as const;
export const TRANSITION_ALIGN_LABELS: Record<(typeof TRANSITION_ALIGNS)[number] | 'custom', string> = {
  center: 'Center at cut',
  start: 'Start at cut',
  end: 'End at cut',
  custom: 'Custom',
};

/** Alignment of a clip's incoming transition (see TransitionAlign). */
export function transitionAlignOf(clip: Partial<Pick<ClipData, 'ext'>>): TransitionAlign {
  return readExt(clip).transitionAlign?.in ?? 'center';
}

/**
 * Window [start, end] of a transition of `durationUs` at a cut: centre
 * offset by `align` (custom offsets are kept within half the duration, so
 * the window always touches the cut).
 */
export function alignedWindow(cutUs: number, durationUs: number, align: TransitionAlign): { startUs: number; endUs: number } {
  const half = durationUs / 2;
  const centre = align === 'start' ? cutUs + half : align === 'end' ? cutUs - half : align === 'center' ? cutUs : cutUs + Math.max(-half, Math.min(half, align));
  return { startUs: centre - half, endUs: centre + half };
}

/**
 * Audio crossfade curves of transitions. Constant power (sin/cos, no dip in
 * loudness for uncorrelated sounds) is what new transitions get; constant
 * gain is the linear ramp every older transition was made with (and stays
 * the meaning of "unset"); exponential falls off fast.
 */
export const AUDIO_CURVES = ['power', 'gain', 'exp'] as const;
export type AudioCurve = (typeof AUDIO_CURVES)[number];
export const AUDIO_CURVE_LABELS: Record<AudioCurve, string> = { power: 'Constant power', gain: 'Constant gain', exp: 'Exponential' };
/** The curve a transition made now gets. */
export const NEW_TRANSITION_AUDIO_CURVE: AudioCurve = 'power';

/** Audio crossfade curve of a clip's incoming or outgoing transition ('gain' when unset: older transitions). */
export function audioCurveOf(clip: Partial<Pick<ClipData, 'ext'>>, dir: 'in' | 'out'): AudioCurve {
  return readExt(clip).audioCrossfade?.[dir] ?? 'gain';
}

/**
 * Gain of the incoming side (`fadeIn`) or the outgoing side of a crossfade
 * at progress p (0..1). The two sides of a constant-power fade keep
 * in² + out² = 1, constant gain keeps in + out = 1.
 */
export function crossfadeGain(curve: string, p: number, fadeIn: boolean): number {
  const x = Math.max(0, Math.min(1, p));
  if (curve === 'power') return fadeIn ? Math.sin((x * Math.PI) / 2) : Math.cos((x * Math.PI) / 2);
  if (curve === 'exp') return fadeIn ? 1 - (1 - x) ** 3 : (1 - x) ** 3;
  return fadeIn ? x : 1 - x;
}

/** Clips of one track sorted by start time. */
export function sortClips<T extends { startUs: number }>(clips: readonly T[]): T[] {
  return [...clips].sort((a, b) => a.startUs - b.startUs);
}

/** Returns the clip directly before `clip` on the same track if they touch. */
export function adjacentPrev<T extends TimedClip>(clip: T, trackClips: readonly T[]): T | null {
  let best: T | null = null;
  for (const c of trackClips) {
    if (c.id === clip.id || c.trackId !== clip.trackId) continue;
    if (Math.abs(clipEnd(c) - clip.startUs) <= TIME_EPS) best = c;
  }
  return best;
}

export function adjacentNext<T extends TimedClip>(clip: T, trackClips: readonly T[]): T | null {
  const end = clipEnd(clip);
  for (const c of trackClips) {
    if (c.id === clip.id || c.trackId !== clip.trackId) continue;
    if (Math.abs(c.startUs - end) <= TIME_EPS) return c;
  }
  return null;
}

function dipOf(clip: TimedClip, dir: 'in' | 'out', atCut: boolean): { dip?: DipMode } {
  const tr = dir === 'in' ? clip.transitionIn : clip.transitionOut;
  return tr && isDipTransition(tr.kind) ? { dip: transitionDipOf(clip, dir, atCut) } : {};
}

/**
 * Transition window for a clip's incoming transition.
 * With an adjacent previous clip the window sits at the cut as aligned
 * (centred by default, see TransitionAlign); otherwise
 * the clip transitions in from transparency during its first `durationUs`.
 */
export function transitionWindow<T extends TimedClip>(clip: T, trackClips: readonly T[]): TransitionWindow | null {
  const tr = clip.transitionIn;
  if (!tr || tr.durationUs <= 0) return null;
  const prev = adjacentPrev(clip, trackClips);
  if (prev) {
    const w = alignedWindow(clip.startUs, tr.durationUs, transitionAlignOf(clip));
    return { toClipId: clip.id, fromClipId: prev.id, kind: tr.kind, startUs: w.startUs, endUs: w.endUs, easing: transitionEasingOf(clip, 'in'), ...dipOf(clip, 'in', true) };
  }
  return { toClipId: clip.id, fromClipId: null, kind: tr.kind, startUs: clip.startUs, endUs: clip.startUs + tr.durationUs, easing: transitionEasingOf(clip, 'in'), ...dipOf(clip, 'in', false) };
}

/**
 * Window of a clip's outgoing transition into transparency, during its last
 * `durationUs`. Only applies when no clip follows directly: a cut into the
 * next clip is that clip's incoming transition. It never overlaps the clip's
 * incoming transition (a trim can leave too little room for both).
 */
export function transitionOutWindow<T extends TimedClip>(clip: T, trackClips: readonly T[]): TransitionWindow | null {
  const tr = clip.transitionOut;
  if (!tr || tr.durationUs <= 0 || adjacentNext(clip, trackClips)) return null;
  const end = clipEnd(clip);
  const own = transitionWindow(clip, trackClips);
  const start = Math.max(end - tr.durationUs, own ? own.endUs : clip.startUs);
  if (start >= end - TIME_EPS) return null;
  return { toClipId: null, fromClipId: clip.id, kind: tr.kind, startUs: start, endUs: end, easing: transitionEasingOf(clip, 'out'), ...dipOf(clip, 'out', false) };
}

/** Every transition window on one track (cuts, in from and out to transparency), by start time. */
export function trackTransitionWindows<T extends TimedClip>(trackClips: readonly T[]): TransitionWindow[] {
  const out: TransitionWindow[] = [];
  for (const c of trackClips) {
    const w = transitionWindow(c, trackClips);
    if (w) out.push(w);
    const o = transitionOutWindow(c, trackClips);
    if (o) out.push(o);
  }
  return out.sort((a, b) => a.startUs - b.startUs);
}

/** Longest outgoing transition that fits after the part of the clip its incoming transition uses. */
export function maxTransitionOutUs<T extends TimedClip>(clip: T, trackClips: readonly T[]): number {
  const w = transitionWindow(clip, trackClips);
  const used = w ? Math.max(0, w.endUs - clip.startUs) : 0;
  return Math.max(0, clip.durationUs - used);
}

/** Maximum transition duration allowed between `prev` and `clip`. */
export function maxTransitionUs(clip: TimedClip, prev: TimedClip | null): number {
  if (!prev) return clip.durationUs;
  return Math.min(clip.durationUs, prev.durationUs);
}

/**
 * Visible span of a clip including the extra time borrowed by transitions
 * (the outgoing clip keeps playing past its end, the incoming clip starts early).
 */
export function clipSpan<T extends TimedClip>(clip: T, trackClips: readonly T[]): { startUs: number; endUs: number } {
  let start = clip.startUs;
  let end = clipEnd(clip);
  const own = transitionWindow(clip, trackClips);
  if (own && own.fromClipId) start = own.startUs;
  for (const c of trackClips) {
    if (c.id === clip.id || c.trackId !== clip.trackId || !c.transitionIn) continue;
    if (Math.abs(c.startUs - end) <= TIME_EPS) {
      const w = transitionWindow(c, trackClips);
      if (w && w.fromClipId === clip.id) end = w.endUs;
    }
  }
  return { startUs: start, endUs: end };
}


// ---------------------------------------------------------------------------
// Overlap validation
// ---------------------------------------------------------------------------

export interface OverlapError {
  trackId: string;
  a: string;
  b: string;
}

/** Clips on the same track may touch but never overlap. */
export function findOverlap(clips: readonly Pick<ClipData, 'id' | 'trackId' | 'startUs' | 'durationUs'>[]): OverlapError | null {
  const byTrack = new Map<string, typeof clips[number][]>();
  for (const c of clips) {
    const list = byTrack.get(c.trackId);
    if (list) list.push(c);
    else byTrack.set(c.trackId, [c]);
  }
  for (const [trackId, list] of byTrack) {
    const sorted = sortClips(list);
    for (let i = 1; i < sorted.length; i++) {
      if (sorted[i].startUs < clipEnd(sorted[i - 1]) - TIME_EPS) {
        return { trackId, a: sorted[i - 1].id, b: sorted[i].id };
      }
    }
  }
  return null;
}

export function projectDurationUs(clips: readonly { startUs: number; durationUs: number }[]): number {
  let end = 0;
  for (const c of clips) end = Math.max(end, clipEnd(c));
  return end;
}

// ---------------------------------------------------------------------------
// Keyframes
// ---------------------------------------------------------------------------

export function keyframesFor(keyframes: readonly Keyframe[], property: string): Keyframe[] {
  return keyframes.filter((k) => k.property === property).sort((a, b) => a.timeUs - b.timeUs);
}

/**
 * Interpolate a keyframed value at `localUs` (offset from clip start).
 * The easing of a keyframe controls the segment that *leaves* it.
 */
export function interpolateKeyframes(sorted: readonly Keyframe[], localUs: number): number | undefined {
  if (sorted.length === 0) return undefined;
  if (localUs <= sorted[0].timeUs) return sorted[0].value;
  const last = sorted[sorted.length - 1];
  if (localUs >= last.timeUs) return last.value;
  for (let i = 0; i < sorted.length - 1; i++) {
    const a = sorted[i];
    const b = sorted[i + 1];
    if (localUs >= a.timeUs && localUs < b.timeUs) {
      const span = b.timeUs - a.timeUs;
      const p = span <= 0 ? 1 : (localUs - a.timeUs) / span;
      if (a.easing === 'smooth') return smoothSegment(sorted, i, p);
      return a.value + (b.value - a.value) * ease(a.easing, p);
    }
  }
  return last.value;
}

/**
 * Cubic Hermite segment between keyframes i and i+1 whose tangents run
 * through the neighbouring keyframes (Catmull-Rom on uneven spacing). Ends
 * without a neighbour get a flat tangent, so motion eases in and out there.
 */
function smoothSegment(sorted: readonly Keyframe[], i: number, p: number): number {
  const a = sorted[i];
  const b = sorted[i + 1];
  const span = b.timeUs - a.timeUs;
  const slope = (j: number) => {
    const prev = sorted[j - 1];
    const next = sorted[j + 1];
    if (!prev || !next) return 0;
    const dt = next.timeUs - prev.timeUs;
    return dt > 0 ? (next.value - prev.value) / dt : 0;
  };
  const m0 = slope(i) * span;
  const m1 = slope(i + 1) * span;
  const p2 = p * p;
  const p3 = p2 * p;
  return (2 * p3 - 3 * p2 + 1) * a.value + (p3 - 2 * p2 + p) * m0 + (-2 * p3 + 3 * p2) * b.value + (p3 - p2) * m1;
}

type AnimatableClip = Pick<ClipData, 'transform' | 'color' | 'volume'> & Partial<Pick<ClipData, 'crop' | 'videoFx' | 'ext' | 'kind' | 'group'>>;

/** Static (non-keyframed) value of a keyframable property. */
export function baseValue(clip: AnimatableClip, property: string): number {
  const scoped = splitNodeProperty(property);
  if (scoped.node !== undefined) return nodeGradeValue(nodeOfProperty(gradeNodes(readExt(clip)), scoped.node)?.grade, scoped.base);
  switch (property) {
    case 'x':
      return clip.transform.x;
    case 'y':
      return clip.transform.y;
    case 'scale':
      return clip.transform.scale;
    case 'rotation':
      return clip.transform.rotation;
    case 'opacity':
      return clip.transform.opacity;
    case 'volume':
      return clip.volume;
    case 'speed':
      return (clip as { speed?: number }).speed || 1;
    case 'brightness':
      return clip.color.brightness;
    case 'contrast':
      return clip.color.contrast;
    case 'saturation':
      return clip.color.saturation;
    case 'gamma':
      return clip.color.gamma;
    case 'hue':
      return clip.color.hue;
    case 'temperature':
      return clip.color.temperature;
    case 'cropLeft':
      return clip.crop?.left ?? 0;
    case 'cropRight':
      return clip.crop?.right ?? 0;
    case 'cropTop':
      return clip.crop?.top ?? 0;
    case 'cropBottom':
      return clip.crop?.bottom ?? 0;
    case 'blur':
      return clip.videoFx?.blur ?? 0;
    case 'vignette':
      return clip.videoFx?.vignette ?? 0;
    case 'filterIntensity':
      return clip.videoFx?.filterIntensity ?? 1;
    case 'tint':
    case 'shadows':
    case 'highlights':
    case 'colorBoost':
    case 'liftMaster':
    case 'gammaMaster':
    case 'gainMaster':
    case 'offsetMaster':
      return nodeGradeValue(firstGrade(clip), property);
    case 'pivot':
      return firstGrade(clip)?.pivot ?? 0.5;
    case 'grain':
    case 'aberration':
      return readExt(clip).fx?.[property] ?? 0;
    case 'glow':
      return readExt(clip).fx?.glow?.amount ?? 0;
    case 'lensDistortion':
      return readExt(clip).fx?.lens ?? 0;
    default: {
      const def = KEYFRAME_PROPERTIES[property as keyof typeof KEYFRAME_PROPERTIES] as { base?: number } | undefined;
      return def?.base ?? 0;
    }
  }
}

/** The first colour node's grade (plain-named grade properties belong to it). */
function firstGrade(clip: Partial<Pick<ClipData, 'ext'>>) {
  return nodeOfProperty(gradeNodes(readExt(clip)), undefined)?.grade;
}

/** A node grade property's static value (masters: the wheel's y). */
function nodeGradeValue(g: import('./grade').GradeData | undefined, base: string): number {
  if (base.endsWith('Master')) return g?.[base.slice(0, -6) as 'lift']?.y ?? 0;
  return g?.[base as 'tint'] ?? 0;
}

/** The clip's own value of `property` at timeline time `t` (its track group's animation left out). */
export function ownPropertyAt(
  clip: AnimatableClip & Pick<ClipData, 'keyframes' | 'startUs'>,
  property: string,
  t: number,
): number {
  const kfs = keyframesFor(clip.keyframes, property);
  const v = interpolateKeyframes(kfs, t - clip.startUs);
  const raw = v === undefined ? baseValue(clip, property) : v;
  const range = keyframeDef(property);
  return range ? Math.min(range.max, Math.max(range.min, raw)) : raw;
}

/** Properties a track group animates (GroupMotion). */
export const GROUP_MOTION_PROPERTIES = ['x', 'y', 'scale', 'rotation', 'opacity'] as const;
export type GroupMotionProperty = (typeof GROUP_MOTION_PROPERTIES)[number];
export const isGroupMotionProperty = (p: string): p is GroupMotionProperty => (GROUP_MOTION_PROPERTIES as readonly string[]).includes(p);

/** Value of a group animation's property at timeline time `t`. */
export function groupMotionAt(m: GroupMotion, property: GroupMotionProperty, t: number): number {
  const v = interpolateKeyframes(keyframesFor(m.keyframes, property), t);
  const raw = v === undefined ? m[property] : v;
  const range = keyframeDef(property);
  return range ? Math.min(range.max, Math.max(range.min, raw)) : raw;
}

/**
 * Value of `property` at timeline time `t`. A clip in an animated track group
 * (clip.group) is placed inside the group: its position is turned, scaled and
 * moved with the group's, its scale, rotation and opacity combine with the
 * group's. Adjustment layers keep covering the canvas and only take the opacity.
 */
export function propertyAt(
  clip: AnimatableClip & Pick<ClipData, 'keyframes' | 'startUs'>,
  property: string,
  t: number,
): number {
  const v = ownPropertyAt(clip, property, t);
  const g = clip.group;
  if (!g || !isGroupMotionProperty(property)) return v;
  if (property === 'opacity') return v * groupMotionAt(g, 'opacity', t);
  if (clip.kind === 'adjustment') return v;
  const gs = groupMotionAt(g, 'scale', t);
  if (property === 'scale') return v * gs;
  const gr = groupMotionAt(g, 'rotation', t);
  if (property === 'rotation') return v + gr;
  const x = property === 'x' ? v : ownPropertyAt(clip, 'x', t);
  const y = property === 'y' ? v : ownPropertyAt(clip, 'y', t);
  const a = (gr * Math.PI) / 180;
  return property === 'x'
    ? groupMotionAt(g, 'x', t) + gs * (x * Math.cos(a) - y * Math.sin(a))
    : groupMotionAt(g, 'y', t) + gs * (x * Math.sin(a) + y * Math.cos(a));
}

/** Whether `property` changes over the clip (its keyframes, or its track group's animation). */
export function propertyAnimated(clip: Pick<ClipData, 'keyframes'> & Partial<Pick<ClipData, 'kind' | 'group'>>, property: string): boolean {
  const own = (p: string) => keyframesFor(clip.keyframes, p).length > 1;
  if (own(property)) return true;
  const g = clip.group;
  if (!g || !isGroupMotionProperty(property)) return false;
  const moves = (p: string) => keyframesFor(g.keyframes, p).length > 1;
  if (property === 'opacity') return moves('opacity');
  if (clip.kind === 'adjustment') return false;
  if (property === 'scale' || property === 'rotation') return moves(property);
  // A position turns with the other axis and moves with every part of the group's transform.
  return moves('x') || moves('y') || moves('scale') || moves('rotation') || own(property === 'x' ? 'y' : 'x');
}

/**
 * Largest `scale` a clip is shown at (over its keyframes, else its static value, times its track
 * group's largest), at least 1.
 */
export function peakScale(clip: AnimatableClip & Pick<ClipData, 'keyframes' | 'startUs'>): number {
  const peak = (kfs: readonly Keyframe[], still: number) => (kfs.length ? Math.max(...kfs.map((k) => k.value)) : still);
  const own = peak(keyframesFor(clip.keyframes, 'scale'), ownPropertyAt(clip, 'scale', clip.startUs));
  const g = clip.group;
  const group = g ? peak(keyframesFor(g.keyframes, 'scale'), g.scale) : 1;
  return Math.max(1, own * group);
}

/**
 * Shapes of an audio clip fade (the gain curve from silent to full; a fade out
 * runs it backwards). 'linear' is what every fade was made with and stays the
 * meaning of "unset".
 */
export const FADE_SHAPE_LABELS: Record<FadeShape, string> = {
  linear: 'Linear',
  fastStart: 'Fast start',
  fastEnd: 'Slow start',
  power: 'Equal power',
  ease: 'Ease in/out',
  scurve: 'S-curve',
  fastEnds: 'Fast start and end',
};

/** Gain of a fade of the given shape at progress p (0 = silent, 1 = full). */
export function fadeShapeGain(shape: string | undefined, p: number): number {
  const x = Math.max(0, Math.min(1, p));
  switch (shape) {
    case 'fastStart':
      return 1 - (1 - x) ** 2;
    case 'fastEnd':
      return x * x;
    case 'power':
      return Math.sin((x * Math.PI) / 2);
    case 'ease':
      return x * x * (3 - 2 * x);
    case 'scurve':
      return x * x * x * (x * (x * 6 - 15) + 10);
    case 'fastEnds':
      return x < 0.5 ? Math.sqrt(x / 2) : 1 - Math.sqrt((1 - x) / 2);
    default:
      return x;
  }
}

/** Fade shape of a clip's audio fade in or out ('linear' when unset). */
export function audioFadeShapeOf(clip: Partial<Pick<ClipData, 'ext'>>, dir: 'in' | 'out'): FadeShape {
  return readExt(clip).audioFadeShape?.[dir] ?? 'linear';
}

/** Fade multiplier (0..1) for a fade-in/out pair at timeline time `t`. */
export function fadeAt(
  clip: Pick<ClipData, 'startUs' | 'durationUs'>,
  fadeInUs: number,
  fadeOutUs: number,
  t: number,
  shapes?: { in?: string; out?: string },
): number {
  let m = 1;
  const local = t - clip.startUs;
  if (fadeInUs > 0 && local < fadeInUs) m = Math.min(m, fadeShapeGain(shapes?.in, local / fadeInUs));
  const remaining = clipEnd(clip) - t;
  if (fadeOutUs > 0 && remaining < fadeOutUs) m = Math.min(m, fadeShapeGain(shapes?.out, remaining / fadeOutUs));
  return m;
}

/** Solo-aware audibility of a track. */
export function trackAudible(track: Pick<TrackData, 'muted' | 'solo'>, anySolo: boolean): boolean {
  if (track.muted) return false;
  return anySolo ? track.solo : true;
}

/** Geometry of a clip on the canvas for its natural media size. */
export interface ClipGeometry {
  /** Center in canvas pixels (origin top-left). */
  cx: number;
  cy: number;
  /** Displayed size (after crop and scale) in canvas pixels. */
  width: number;
  height: number;
  rotation: number;
  opacity: number;
}

/**
 * Size a media frame of `mediaW`×`mediaH` occupies at scale 1: fitted inside
 * the canvas for footage/images; text and color clips use their own pixels.
 */
export function fitScale(kind: string, mediaW: number, mediaH: number, canvasW: number, canvasH: number): number {
  if (kind === 'text' || kind === 'color' || kind === 'shape' || kind === 'particles' || kind === 'adjustment' || mediaW <= 0 || mediaH <= 0) return 1;
  return Math.min(canvasW / mediaW, canvasH / mediaH);
}

/** Crop (fractions of the source removed per side) at timeline time `t`, keyframes included. */
export function cropAt(clip: Pick<ClipData, 'transform' | 'crop' | 'color' | 'volume' | 'keyframes' | 'startUs'>, t: number): Crop {
  return {
    left: propertyAt(clip, 'cropLeft', t),
    right: propertyAt(clip, 'cropRight', t),
    top: propertyAt(clip, 'cropTop', t),
    bottom: propertyAt(clip, 'cropBottom', t),
  };
}

export function clipGeometry(
  clip: Pick<ClipData, 'kind' | 'transform' | 'crop' | 'color' | 'volume' | 'keyframes' | 'startUs'>,
  mediaW: number,
  mediaH: number,
  canvasW: number,
  canvasH: number,
  t: number,
): ClipGeometry {
  // Adjustment layers always cover the whole canvas.
  if (clip.kind === 'adjustment') return { cx: canvasW / 2, cy: canvasH / 2, width: canvasW, height: canvasH, rotation: 0, opacity: propertyAt(clip, 'opacity', t) };
  const s0 = fitScale(clip.kind, mediaW, mediaH, canvasW, canvasH);
  const scale = propertyAt(clip, 'scale', t);
  const crop = isCroppable(clip.kind) ? cropAt(clip, t) : { left: 0, right: 0, top: 0, bottom: 0 };
  const cw = mediaW * Math.max(0, 1 - crop.left - crop.right);
  const ch = mediaH * Math.max(0, 1 - crop.top - crop.bottom);
  return {
    cx: canvasW / 2 + propertyAt(clip, 'x', t),
    cy: canvasH / 2 + propertyAt(clip, 'y', t),
    width: cw * s0 * scale,
    height: ch * s0 * scale,
    rotation: propertyAt(clip, 'rotation', t),
    opacity: propertyAt(clip, 'opacity', t),
  };
}

/** Clip kinds whose media can be cropped. */
export function isCroppable(kind: string): boolean {
  return kind === 'video' || kind === 'image' || kind === 'shape';
}

/** Camera distance for 3D tilt, in multiples of the tilted box's larger side. */
export const TILT_CAMERA = 2.5;

export interface TiltCorner {
  /** Projected position relative to the box centre (same units as w/h). */
  x: number;
  y: number;
  /** Perspective factor (camera distance / depth): > 1 nearer, < 1 farther. */
  q: number;
}

/**
 * Corners (top-left, top-right, bottom-left, bottom-right) of a w×h box
 * centred on 0 after tilting it in 3D and projecting it back with a camera
 * TILT_CAMERA × max(w, h) away. Positive tiltX tips the top edge away from the
 * viewer, positive tiltY the right edge. The camera distance is relative to
 * the box, so the projection scales with it (scale animations commute).
 */
export function tiltCorners(w: number, h: number, tiltXDeg: number, tiltYDeg: number): TiltCorner[] {
  const ax = (tiltXDeg * Math.PI) / 180;
  const ay = (tiltYDeg * Math.PI) / 180;
  const d = TILT_CAMERA * Math.max(w, h, 1e-6);
  return [
    [-w / 2, -h / 2],
    [w / 2, -h / 2],
    [-w / 2, h / 2],
    [w / 2, h / 2],
  ].map(([x, y]) => {
    // Tilt around the horizontal axis (top recedes), then the vertical axis (right recedes).
    const y1 = y * Math.cos(ax);
    const z1 = -y * Math.sin(ax);
    const x2 = x * Math.cos(ay) - z1 * Math.sin(ay);
    const z2 = x * Math.sin(ay) + z1 * Math.cos(ay);
    const q = d / (d + z2);
    return { x: x2 * q, y: y1 * q, q };
  });
}

/** Whether a clip is tilted at any time (keyframes or not). */
export function hasTilt(clip: Pick<ClipData, 'keyframes'>): boolean {
  return clip.keyframes.some((k) => (k.property === 'tiltX' || k.property === 'tiltY') && Math.abs(k.value) > 1e-6);
}

/** Which clip kinds may live on which track kinds. */
export function clipFitsTrack(clipKind: string, trackKind: string): boolean {
  if (trackKind === 'audio') return clipKind === 'audio';
  return VISUAL_CLIP_KINDS.includes(clipKind);
}

/** Clip kinds that go on video tracks. */
export const VISUAL_CLIP_KINDS = ['video', 'image', 'text', 'color', 'shape', 'particles', 'adjustment'];

export function hasTransition(t: TransitionRef | null | undefined): t is TransitionRef {
  return !!t && t.durationUs > 0;
}

/**
 * Audio gain of a clip at timeline time `t` (without track volume):
 * volume keyframes × audio fades × transition crossfade ramps, each on the
 * transition's audio curve (see crossfadeGain; the preview and the export
 * both sample this).
 */
export function clipAudioGainAt(
  clip: Pick<ClipData, 'id' | 'trackId' | 'startUs' | 'durationUs' | 'transitionIn' | 'transitionOut' | 'transform' | 'color' | 'volume' | 'keyframes' | 'audioFadeInUs' | 'audioFadeOutUs' | 'muted'> & Partial<Pick<ClipData, 'ext'>>,
  trackClips: readonly (Pick<ClipData, 'id' | 'trackId' | 'startUs' | 'durationUs' | 'transitionIn' | 'transitionOut'> & Partial<Pick<ClipData, 'ext'>>)[],
  t: number,
): number {
  if (clip.muted) return 0;
  let g = propertyAt(clip, 'volume', t) * fadeAt(clip, clip.audioFadeInUs, clip.audioFadeOutUs, t, readExt(clip).audioFadeShape);
  const progress = (w: TransitionWindow) => (t - w.startUs) / (w.endUs - w.startUs);
  const own = transitionWindow(clip as any, trackClips as any);
  if (own && t < own.endUs) g *= crossfadeGain(audioCurveOf(clip, 'in'), progress(own), true);
  const out = transitionOutWindow(clip as any, trackClips as any);
  if (out && t > out.startUs) g *= crossfadeGain(audioCurveOf(clip, 'out'), progress(out), false);
  for (const other of trackClips) {
    if (other.id === clip.id || !other.transitionIn) continue;
    const w = transitionWindow(other as any, trackClips as any);
    if (w && w.fromClipId === clip.id && t > w.startUs) g *= crossfadeGain(audioCurveOf(other, 'in'), progress(w), false);
  }
  return g;
}

/** Visual opacity of a clip at time `t`: opacity keyframes × video fades. */
export function clipAlphaAt(
  clip: Pick<ClipData, 'startUs' | 'durationUs' | 'transform' | 'color' | 'volume' | 'keyframes' | 'videoFadeInUs' | 'videoFadeOutUs'>,
  t: number,
): number {
  return propertyAt(clip, 'opacity', t) * fadeAt(clip, clip.videoFadeInUs, clip.videoFadeOutUs, t);
}
